import React from 'react';
import { BarChart3 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';

import { useChartColors } from './useChartColors';

export interface HorizontalBarChartDatum {
    label: string;
    value: number;
}

export interface HorizontalBarChartProps {
    data: HorizontalBarChartDatum[];
    /** Formats a raw value for the tooltip and the screen-reader summary. */
    formatValue?: (value: number) => string;
    height?: number;
    className?: string;
    emptyTitle?: string;
    emptyDescription?: string;
}

/**
 * A small horizontal bar chart for stage/category counts (e.g. lead funnel
 * stages, trade outcome totals). Themed via the `--chart-*` tokens through
 * `useChartColors` rather than a hard-coded hex.
 */
export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
    data,
    formatValue = value => String(value),
    height = 200,
    className,
    emptyTitle = 'No data yet',
    emptyDescription,
}) => {
    const colors = useChartColors();
    const hasData = data.some(item => item.value > 0);

    if (!hasData) {
        return (
            <EmptyState
                icon={BarChart3}
                title={emptyTitle}
                description={emptyDescription}
                size="sm"
                className={className}
            />
        );
    }

    const summary = data.map(item => `${item.label}: ${formatValue(item.value)}`).join(', ');

    return (
        <div className={cn('w-full', className)}>
            <div role="img" aria-label={summary} style={{ width: '100%', height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-border"
                            horizontal={false}
                        />
                        <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                        <YAxis
                            type="category"
                            dataKey="label"
                            width={100}
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip formatter={(value: number) => formatValue(value)} />
                        <Bar dataKey="value" fill={colors.chart1} radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default HorizontalBarChart;
